/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */
import './style/assignInvoiceNumber.scss';

import React, {FocusEventHandler, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {ChangeFunction, ErrorType, Loader} from '@dvrd/dvr-controls';

import Invoice from '../../../models/invoiceModel';

import {BolmateDialog, BolmateInput, BolmateNumberInput} from '../../controls';

import classNames from 'classnames';

import {AppContext} from '../../misc/appContext';

import {replaceDateVariables} from '../../../util/utils';

interface Props {
    onChangePrefix: ChangeFunction<string>;
    onChangeNum: ChangeFunction<string>;
    onClose: VoidFunction;
    onSubmit: VoidFunction;
    onResetError: FocusEventHandler;
    invoice: Invoice;
    num: string;
    prefix: string;
    active: boolean;
    loading: boolean;
    error: ErrorType;
    accountPrefix: string;
    latestNum: string;
}

export default function AssignInvoiceNumber(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {
        invoice,
        num,
        active,
        onChangeNum,
        onResetError,
        error,
        loading,
        onSubmit,
        onClose,
        onChangePrefix,
        prefix,
        accountPrefix,
        latestNum
    } = props;

    function getLastUsed(): string | null {
        const {account} = context.accountContext;
        if (!account) return null;
        return account.getFlex('last_used_invoice_number') ?? null;
    }

    function getCurrentSetting(): string | null {
        const {account} = context.accountContext;
        const settings = account?.settings;
        if (!settings) return null;
        const prefix = replaceDateVariables(settings.invoicePrefix);
        return prefix + latestNum;
    }

    function renderInvoiceData() {
        const currentSetting = getCurrentSetting();
        const lastUsed = getLastUsed();
        return (
            <div className='invoice-data-container'>
                <label className='data-title'>{t('dashboard.assignNum.invoiceData', 'Factuurgegevens')}</label>
                <div className='fields'>
                    {renderField(t('dashboard.assignNum.orderNumber', 'Ordernummer'), invoice.orderId)}
                    {renderField(
                        t('dashboard.assignNum.orderDate', 'Orderdatum'),
                        invoice.momentDate.format('DD-MM-YYYY')
                    )}
                    {renderField(t('dashboard.assignNum.reference', 'Referentie'), invoice.reference)}
                    {renderField(t('dashboard.assignNum.customerType', 'Type klant'), invoice.translatedCustomerType)}
                    <div className='field full mt'>
                        <label>
                            <b>{t('dashboard.assignNum.latestNormalSeries', 'Laatste nummer normale reeks:')}</b>{' '}
                            {currentSetting}
                        </label>
                    </div>
                    {!!lastUsed && (
                        <div className='field full'>
                            <label>
                                <b>{t('dashboard.assignNum.lastTimeFilled', 'Laatste keer vulde je in:')}</b> {lastUsed}
                            </label>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderField(label: string, value: string, fullRow: boolean = false) {
        if (!value) value = '-';
        return (
            <div className={classNames('field', fullRow && 'full')}>
                <label className='label'>{label}</label>
                <label className='value'>{value}</label>
            </div>
        );
    }

    return (
        <BolmateDialog
            active={active}
            onClose={onClose}
            title={t('dashboard.assignNum.title', 'Factuurnummer kiezen')}
            className='assign-invoice-number'
            width='40rem'
            onCancel={onClose}
            cancelLabel={t('common.cancel', 'Annuleren')}
            cancelVariant='tertiary'
            onConfirm={onSubmit}
            confirmLabel={t('common.apply', 'Toepassen')}
            loading={loading}
        >
            <Loader active={loading} />
            <p className='message'>
                {t(
                    'dashboard.assignNum.message',
                    'Kies een factuurnummer voor deze factuur. Het factuurnummer mag niet eerder gebruikt zijn en de facturen moeten met oplopende factuurnummers genummerd worden.'
                )}
            </p>
            {renderInvoiceData()}
            <div className='input-row'>
                <BolmateInput
                    onChange={onChangePrefix}
                    value={prefix}
                    label={t('dashboard.assignNum.prefix', 'Voorvoegsel')}
                    inputProps={{
                        placeholder: `${t('dashboard.assignNum.egPlaceholder', 'Bijv.')} ${accountPrefix}`
                    }}
                    onFocus={onResetError}
                    shape='rounded'
                    uniformFont
                />
                <BolmateNumberInput
                    onChange={onChangeNum}
                    value={num}
                    label={t('dashboard.assignNum.invoiceNumberReq', 'Factuurnummer *')}
                    inputProps={{placeholder: t('dashboard.assignNum.invoiceNumberPh', 'Factuurnummer')}}
                    onFocus={onResetError}
                    wholeNumbers
                    shape='rounded'
                    uniformFont
                />
            </div>
            <label className={classNames('error', !error && 'hidden')}>{error}</label>
        </BolmateDialog>
    );
}
